import Recorder from 'opus-recorder'
import encoderPath from 'opus-recorder/dist/encoderWorker.min.js?url'

const OGG_MIME = 'audio/ogg; codecs=opus'
const MIME_CANDIDATES = [
  'audio/ogg;codecs=opus',
  'audio/ogg',
  'audio/webm;codecs=opus',
  'audio/webm'
]
const RECORDING_TIMESLICE_MS = 250
const MEDIA_RECORDER_BITRATE = 160000

const AUDIO_TRACK_CONSTRAINTS = {
  channelCount: 1,
  echoCancellation: true,
  noiseSuppression: false,
  autoGainControl: true,
  sampleRate: 48000
}

function buildOpusRecorderConfig() {
  return {
    encoderPath,
    numberOfChannels: 1,
    encoderSampleRate: 48000,
    encoderApplication: 2048,
    encoderBitRate: 96000,
    encoderComplexity: 10,
    resampleQuality: 10,
    maxFramesPerPage: 40,
    streamPages: false,
    recordingGain: 1.1,
    mediaTrackConstraints: {
      audio: AUDIO_TRACK_CONSTRAINTS
    }
  }
}

function logRecordingResult(mode, blob, durationSeconds) {
  const size = blob?.size ?? 0
  const kbps = durationSeconds > 0 ? Math.round((size * 8) / durationSeconds / 1000) : 0
  console.info('[whatsappVoiceRecorder]', {
    mode,
    bytes: size,
    durationSeconds,
    estimatedKbps: kbps,
    mimeType: mode === 'opus' ? OGG_MIME : blob?.type
  })
}

/** @type {{ kind: 'opus', recorder: import('opus-recorder').default, startedAt: number } | { kind: 'media', mediaRecorder: MediaRecorder, stream: MediaStream, chunks: Blob[], mimeType: string, startedAt: number } | null} */
let session = null

function clearSession() {
  session = null
}

export function isWhatsAppVoiceRecordingActive() {
  return !!session
}

async function startMediaRecorderFallback(startedAt) {
  const stream = await navigator.mediaDevices.getUserMedia({
    audio: AUDIO_TRACK_CONSTRAINTS
  })

  let mimeType = ''
  for (const candidate of MIME_CANDIDATES) {
    if (MediaRecorder.isTypeSupported(candidate)) {
      mimeType = candidate
      break
    }
  }

  const recorderOptions = { mimeType: mimeType || undefined, audioBitsPerSecond: MEDIA_RECORDER_BITRATE }
  if (!recorderOptions.mimeType) delete recorderOptions.mimeType

  let mediaRecorder
  try {
    mediaRecorder = new MediaRecorder(stream, recorderOptions)
  } catch {
    mediaRecorder = mimeType
      ? new MediaRecorder(stream, { mimeType })
      : new MediaRecorder(stream)
  }

  const chunks = []
  mediaRecorder.ondataavailable = (event) => {
    if (event.data?.size) chunks.push(event.data)
  }
  mediaRecorder.start(RECORDING_TIMESLICE_MS)

  session = {
    kind: 'media',
    mediaRecorder,
    stream,
    chunks,
    mimeType: mediaRecorder.mimeType || mimeType || 'audio/webm',
    startedAt
  }
  console.info('[whatsappVoiceRecorder] modo: media', { mimeType: session.mimeType })
}

export async function startWhatsAppVoiceRecording() {
  if (session) throw new Error('Gravação já em andamento')
  const startedAt = Date.now()

  try {
    const recorder = new Recorder(buildOpusRecorderConfig())
    await recorder.start()
    session = { kind: 'opus', recorder, startedAt }
    console.info('[whatsappVoiceRecorder] modo: opus')
    return
  } catch (err) {
    console.warn('[whatsappVoiceRecorder] opus-recorder indisponível, usando MediaRecorder', err)
  }

  await startMediaRecorderFallback(startedAt)
}

export async function stopWhatsAppVoiceRecording() {
  if (!session) throw new Error('Nenhuma gravação ativa')

  const { startedAt } = session
  const durationSeconds = Math.max(1, Math.ceil((Date.now() - startedAt) / 1000))

  if (session.kind === 'opus') {
    const { recorder } = session
    const blob = await new Promise((resolve, reject) => {
      recorder.ondataavailable = (arrayBuffer) => {
        resolve(new Blob([arrayBuffer], { type: 'audio/ogg' }))
      }
      recorder.onerror = (err) => reject(err || new Error('opus-recorder falhou'))
      try {
        recorder.stop()
      } catch (err) {
        reject(err)
      }
    })
    try {
      recorder.close()
    } catch {
      /* ignore */
    }
    clearSession()
    logRecordingResult('opus', blob, durationSeconds)
    return { blob, mimeType: OGG_MIME, durationSeconds }
  }

  const { mediaRecorder, stream, chunks, mimeType } = session
  await new Promise((resolve, reject) => {
    mediaRecorder.onstop = () => resolve()
    try {
      if (mediaRecorder.state === 'recording') mediaRecorder.requestData()
      mediaRecorder.stop()
    } catch (err) {
      reject(err)
    }
  })
  stream.getTracks().forEach((track) => track.stop())

  if (chunks.length === 0) {
    await new Promise((resolve) => setTimeout(resolve, 50))
  }

  const blob = new Blob(chunks, { type: mimeType })
  clearSession()
  logRecordingResult('media', blob, durationSeconds)
  return { blob, mimeType, durationSeconds }
}

export function cancelWhatsAppVoiceRecording() {
  if (!session) return

  if (session.kind === 'opus') {
    const { recorder } = session
    try {
      recorder.ondataavailable = () => {}
      recorder.stop()
      recorder.close()
    } catch {
      /* ignore */
    }
  } else {
    const { mediaRecorder, stream } = session
    try {
      if (mediaRecorder?.state !== 'inactive') {
        mediaRecorder.onstop = null
        mediaRecorder.stop()
      }
    } catch {
      /* ignore */
    }
    stream?.getTracks().forEach((track) => track.stop())
  }

  clearSession()
}
